import React, { useState, useEffect, useRef, useCallback } from "react";
import { browserHistory } from "react-router";
import { IconButton } from "@material-ui/core";
import { Delete } from "@material-ui/icons";

import ConfirmationModal from "../../shared/ConfirmationModal";
import ExportCSVButton from "../../shared/ExportCSVButton";
import PaginatedTable from "../../shared/PaginatedTable";
import AddChatLocationModal from "./AddChatLocationModal";

import ChabadHousesApi from "../../../services/resources/ChabadHousesApi";
import ChatApi from "../../../services/resources/ChatApi";
import { ApiCallErrorMessageHandler } from "../../../lib/coc-common-scripts";
import { formatFullName } from "../../../lib";
import axios from "axios";
import moment from "moment";
import debounce from "lodash.debounce";

const sortOptions = [
  { id: "chabadHouseName", name: "Chabad House Name" },
  { id: "campusName", name: "Campus Name" },
  { id: "shliachName", name: "Shliach Name" },
  { id: "shluchaName", name: "Shlucha Name" },
  { id: "addedOn", name: "Added On" },
];

const resultsPerPage = 12;

export function ChatLocations({ allowEdit }) {
  // locations list state
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(sortOptions[0].value);
  const [filters, setFilters] = useState({ keyword: "" });
  const [locations, setLocations] = useState([]);
  const [totalLocations, setTotalLocations] = useState(0);

  // add/remove location state
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [removeLocation, setRemoveLocation] = useState(null);
  const [removeLocationLoading, setRemoveLocationLoading] = useState(false);
  const [removeLocationErrorMessage, setRemoveLocationErrorMessage] =
    useState(null);

  // chabad houses list state
  const [chabadHousesList, setChabadHousesList] = useState([]);
  const [chabadHousesListErrorMessage, setChabadHousesListErrorMessage] =
    useState("");
  const [chabadHousesListLoading, setChabadHousesListLoading] = useState(false);

  const apiSignalRef = useRef(axios.CancelToken.source());

  useEffect(() => {
    getChabadHousesList();

    const _apiSignal = apiSignalRef.current;
    return () => _apiSignal.cancel();

    // run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getChabadHousesList = useCallback(async () => {
    try {
      setChabadHousesListErrorMessage("");
      setChabadHousesListLoading(true);

      const chabadHousesListData = await ChabadHousesApi.getChabadHousesDetails(
        apiSignalRef.current.token,
      );

      setChabadHousesList(chabadHousesListData);
      setChabadHousesListLoading(false);
    } catch (err) {
      if (!axios.isCancel(err)) {
        setChabadHousesListErrorMessage(
          ApiCallErrorMessageHandler(
            err,
            "Sorry, something went wrong and we could not retrieve available locations. Please try again.",
          ),
        );
        setChabadHousesListLoading(false);
      }
    }
  }, []);

  const getLocations = useCallback(
    async (
      pageNum = 1,
      numResults = resultsPerPage,
      sortByValue = sortBy,
      filtersValue = filters,
    ) => {
      setErrorMessage("");
      setLoading(true);
      setPage(pageNum);
      setSortBy(sortByValue);

      try {
        const { enrollments, totalEnrollments } =
          await ChatApi.getChatEnrollments(
            pageNum,
            numResults,
            sortByValue,
            filtersValue,
            apiSignalRef.current.token,
          );

        setLoading(false);
        setLocations(enrollments);
        setTotalLocations(totalEnrollments);
      } catch (err) {
        if (!axios.isCancel(err)) {
          setErrorMessage(ApiCallErrorMessageHandler(err));
          setLoading(false);
          setLocations([]);
          setTotalLocations(0);
        }
      }
    },
    [filters, sortBy],
  );

  const filterLocations = useCallback(
    (newFilters) => {
      setFilters(newFilters);
      getLocations(undefined, undefined, undefined, newFilters);
    },
    [getLocations],
  );

  const getLocationsForExport = useCallback(async () => {
    const locationsForExport = await ChatApi.getChatEnrollments(
      undefined,
      undefined,
      sortBy,
      filters,
      apiSignalRef.current.token,
      true,
    );

    return locationsForExport.enrollments;
  }, [filters, sortBy]);

  const handleAddLocation = useCallback(
    async (chabadHouseId, enrollmentOptions) => {
      const addLocationResponse = {};

      try {
        await ChatApi.addChatEnrollment(
          chabadHouseId,
          enrollmentOptions,
          apiSignalRef.current.token,
        );
        addLocationResponse.success = true;

        // reload locations
        getLocations();
      } catch (err) {
        if (!axios.isCancel(err)) {
          addLocationResponse.error = err;
          addLocationResponse.errorMessage = ApiCallErrorMessageHandler(err);
        }
      }

      return addLocationResponse;
    },
    [getLocations],
  );

  const handleRemoveLocation = useCallback(async () => {
    setRemoveLocationErrorMessage("");
    setRemoveLocationLoading(true);

    try {
      await ChatApi.removeChatEnrollment(
        removeLocation.chabadHouseID,
        apiSignalRef.current.token,
      );

      // reload locations
      getLocations(page);

      setRemoveLocation(null);
      setRemoveLocationLoading(false);
    } catch (err) {
      if (!axios.isCancel(err)) {
        setRemoveLocationErrorMessage(ApiCallErrorMessageHandler(err));
        setRemoveLocationLoading(false);
      }
    }
  }, [removeLocation, page, getLocations]);

  return (
    <>
      <div className="flex flex-justify-space flex-align-center mb-24">
        <p className="xxl-text fw-500">Locations</p>
        {allowEdit && (
          <Actions
            getLocationsForExport={getLocationsForExport}
            onAddLocation={() => setShowAddLocationModal(true)}
          />
        )}
      </div>
      <div className="chat-locations-table">
        <PaginatedTable
          filterComponent={<Filters applyFilters={filterLocations} />}
          loading={loading}
          loadData={getLocations}
          page={page}
          records={locations}
          renderHeader={() => <Header />}
          renderRow={(location, index) => (
            <Row
              allowEdit={allowEdit}
              key={index}
              location={location}
              onRemoveLocation={() => setRemoveLocation(location)}
            />
          )}
          results={resultsPerPage}
          showResultsCount={true}
          sortBy={sortBy}
          sortOptions={sortOptions}
          success={!errorMessage}
          totalCount={totalLocations}
        />
      </div>

      {showAddLocationModal && (
        <AddChatLocationModal
          chabadHousesList={chabadHousesList}
          chabadHousesListLoading={chabadHousesListLoading}
          chabadHousesListErrorMessage={chabadHousesListErrorMessage}
          close={() => setShowAddLocationModal(false)}
          submit={handleAddLocation}
        />
      )}

      {!!removeLocation && (
        <ConfirmationModal
          cancel={() => {
            setRemoveLocation(null);
            setRemoveLocationErrorMessage("");
          }}
          confirm={handleRemoveLocation}
          errorMessage={removeLocationErrorMessage}
          loading={removeLocationLoading}
          message={`Are you sure you'd like to remove ${
            removeLocation ? removeLocation.chabadHouseName : "this location"
          }'s access to the Chat Community? This will deactivate all chat members and groups for this Chabad House.`}
          show={true}
        />
      )}
    </>
  );
}

function Filters({ applyFilters }) {
  const [keyword, setKeyword] = useState("");

  const debouncedApplyFilters = useRef(debounce(applyFilters, 500));
  const isInitialMount = useRef(true);

  useEffect(() => {
    // run effect when filters are updated - exclude initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    debouncedApplyFilters.current({ keyword });
  }, [keyword]);

  return (
    <div className="search-input">
      <input
        type="text"
        placeholder="Search Shliach or Chabad House name"
        name="keyword"
        onChange={(event) => setKeyword(event.target.value)}
        value={keyword}
      />
      <i className="material-icons accent-text-secondary flex flex-align-center">
        search
      </i>
    </div>
  );
}

function Actions({ getLocationsForExport, onAddLocation }) {
  const getExportHeaders = () => {
    return [
      { label: "Chabad House ID", value: "chabadHouseID" },
      { label: "Chabad House", value: "chabadHouseName" },
      { label: "Campus ID", value: "campusID" },
      { label: "Campus", value: "campusName" },
      { label: "Shliach ID", value: "shliachID" },
      { label: "Shliach First Name", value: "shliachFirstName" },
      { label: "Shliach Last Name", value: "shliachLastName" },
      { label: "Shlucha ID", value: "shluchaID" },
      { label: "Shlucha First Name", value: "shluchaFirstName" },
      { label: "Shlucha Last Name", value: "shluchaLastName" },
      { label: "Shluchim Members", value: "shluchimMembersCount" },
      { label: "Student Members", value: "studentMembersCount" },
      {
        label: "Added On",
        value: ({ addedOn }) => moment(addedOn).format("MM/DD/YYYY"),
      },
    ];
  };

  return (
    <div className="flex" style={{ columnGap: "16px" }}>
      <button
        className="btn custom-btn btn-accent flex flex-align-center"
        onClick={onAddLocation}
      >
        Add
      </button>
      <ExportCSVButton
        className="btn custom-btn btn-accent flex flex-align-center"
        fileName="Chat_Community_Locations"
        getExportData={getLocationsForExport}
        getHeaders={getExportHeaders}
      />
    </div>
  );
}

function Header() {
  const columns = [
    "Chabad House",
    "Campus",
    "Shliach",
    "Shlucha",
    "Added On",
    "Shluchim Members",
    "Student Members",
    "",
  ];

  return (
    <div className="chat-locations-table-header">
      {columns.map((column, index) => (
        <p className="flex flex-align-center" key={index}>
          {column}
        </p>
      ))}
    </div>
  );
}

function Row(props) {
  const {
    allowEdit,
    location: {
      addedOn,
      campusName,
      chabadHouseID,
      chabadHouseName,
      shliachFirstName,
      shliachID,
      shliachLastName,
      shluchaFirstName,
      shluchaID,
      shluchimMembersCount,
      studentMembersCount,
    },
    onRemoveLocation,
  } = props;

  return (
    <div
      className="chat-locations-table-row"
      onClick={() =>
        browserHistory.push(
          `/shluchim/${shliachID || shluchaID}/chabadHouse/${chabadHouseID}`,
        )
      }
    >
      <p>{chabadHouseName}</p>
      <p>{campusName}</p>
      <p>{formatFullName(shliachFirstName, shliachLastName)}</p>
      <p>{formatFullName(shluchaFirstName, shliachLastName)}</p>
      <p>{moment(addedOn).format("MM/DD/YYYY")}</p>
      <p className={shluchimMembersCount === 0 ? "error-text" : ""}>
        {shluchimMembersCount}
      </p>
      <p>{studentMembersCount}</p>
      <p>
        <IconButton
          disabled={!allowEdit}
          onClick={(e) => {
            e.stopPropagation();
            onRemoveLocation();
          }}
        >
          <Delete fontSize="large" />
        </IconButton>
      </p>
    </div>
  );
}
